/**
 * The client half of the commitment-consent contract.
 *
 * Before a commitment membership can be bought, the server requires proof that
 * the buyer was shown a specific piece of consumer-rights wording and agreed to
 * it. The proof is a content hash, and the server computes its own hash over
 * the exact bytes it published — so the browser's only honest job is to render
 * what it was served and echo back the identifiers that came with it. Nothing
 * here may retype, normalize, trim or otherwise touch the copy: a single
 * changed character is a hash mismatch, and a hash that matches text the buyer
 * did not read is the defect the whole mechanism exists to rule out.
 *
 * Everything in this file is pure. The callable that fetches the copy lives in
 * `store/ConsentActions.js`; keeping the mapping and the vocabulary out of it
 * means both can be unit-tested without booting Firebase.
 */

import { CRD_CONSENT_SCOPES, GYM_TERMS_REFUSAL_REASONS } from "@lemonadetraining/contracts";
import type {
  CrdConsentScope,
  GymTermsRefusalDetails,
  GymTermsRefusalReason,
} from "@lemonadetraining/contracts";

/**
 * The locale the checkout asks for.
 *
 * THIS IS THE LOCALE SEAM. These apps carry no i18n framework and expose no
 * user language preference, so there is exactly one honest answer to send
 * today. The callable takes the locale as an argument and the server already
 * publishes Finnish, so switching is a matter of sourcing this value from a
 * real preference — not of changing any of the machinery below.
 *
 * It must never be defaulted or substituted: serving English copy to a buyer
 * who asked for Finnish would record consent to text they never saw, which is
 * precisely why the server refuses an unsupported locale rather than falling
 * back to a neighbouring one.
 */
export const CHECKOUT_CONSENT_LOCALE = "en";

/* The server's own vocabulary, re-exported under the name this file's callers
   already use. Not restated: a second copy is what let a rename here draw no
   checkbox and refuse every monthly checkout (LT-1637). */
export const CONSENT_COPY_SCOPE = CRD_CONSENT_SCOPES;

export type ConsentCopyScope = CrdConsentScope;

/** The consent copy as the server publishes it. Rendered verbatim, never edited. */
export interface ConsentCopy {
  locale: string;
  consentWording: string;
  consentWordingVersion: string;
  consentWordingHash: string;
  withdrawalInfo: string;
  withdrawalInfoVersion: string;
  withdrawalInfoHash: string;
  scope?: ConsentCopyScope;
}

/**
 * The consent claim as `createStripeCheckoutSession` expects to receive it.
 *
 * The field names differ from the ones the copy arrives under, and that rename
 * happens HERE and nowhere else. Two shapes for one concept is exactly the kind
 * of seam where a caller sends `consentWordingVersion` and a server reads
 * `wordingVersion` and neither ever notices; one mapping function is the whole
 * mitigation.
 */
export interface ConsentEchoPayload {
  wordingVersion: string;
  withdrawalInfoVersion: string;
  wordingContentHash: string;
  withdrawalInfoContentHash: string;
  locale: string;
}

/**
 * Turn served copy into the claim echoed back at checkout.
 *
 * Only identifiers cross this boundary — never the text. The server holds the
 * canonical wording; sending it back would invite a client that "helpfully"
 * repaired a mismatch instead of surfacing one.
 */
export function consentEchoPayload(copy: ConsentCopy): ConsentEchoPayload {
  return {
    wordingVersion: copy.consentWordingVersion,
    withdrawalInfoVersion: copy.withdrawalInfoVersion,
    wordingContentHash: copy.consentWordingHash,
    withdrawalInfoContentHash: copy.withdrawalInfoHash,
    locale: copy.locale,
  };
}

/**
 * Whether a served payload is complete enough to render and echo.
 *
 * A partially-populated response is a contract break, not a buyer-facing state:
 * rendering an empty consent label would put an unlabelled checkbox in front of
 * someone about to sign a year-long contract. Treated as a fetch failure so it
 * takes the honest error path rather than settling in quietly.
 */
export function isUsableConsentCopy(copy: unknown): copy is ConsentCopy {
  if (!copy || typeof copy !== "object") return false;
  const fields: Array<keyof ConsentCopy> = [
    "locale",
    "consentWording",
    "consentWordingVersion",
    "consentWordingHash",
    "withdrawalInfo",
    "withdrawalInfoVersion",
    "withdrawalInfoHash",
  ];
  const record = copy as Record<string, unknown>;
  return fields.every((field) => typeof record[field] === "string" && record[field] !== "");
}

/**
 * The refusal tokens the server attaches to `error.details.reason`. The gate
 * and the copy endpoint share this vocabulary deliberately, so the checkout
 * needs one set of responses rather than two kept in step by hand.
 */
export const CONSENT_REASON = {
  missing: "consent_missing",
  malformed: "consent_malformed",
  versionUnknown: "consent_version_unknown",
  hashMismatch: "consent_hash_mismatch",
  localeUnsupported: "consent_locale_unsupported",
  localeInvalid: "consent_locale_invalid",
} as const;

export type ConsentReason = (typeof CONSENT_REASON)[keyof typeof CONSENT_REASON];

/** How the checkout should respond to a refusal. */
export type ConsentRecovery =
  /** The terms moved under us: fetch them again, ask again. No reload, no lost cart. */
  | "refetch"
  /** Nothing was sent, or not in a usable shape: send the buyer back to the checkbox. */
  | "reconfirm"
  /** This language cannot be sold in. Say so; retrying changes nothing. */
  | "locale"
  /** Not a consent refusal at all — the caller's generic error path owns it. */
  | null;

/**
 * Read a consent refusal out of a callable error.
 *
 * Anything without one of the known tokens returns null, which is what keeps
 * every unrelated failure on the generic notification path it has always taken.
 */
export function consentReasonOf(error: unknown): ConsentReason | null {
  return refusalTokenOf(error, Object.values(CONSENT_REASON)) as ConsentReason | null;
}

/* The callable error envelope is one seam, not two: both instruments read
   `error.details.reason` and differ only in which tokens they answer to. */
function refusalTokenOf(error: unknown, known: readonly string[]): string | null {
  const details = (error as { details?: unknown } | null | undefined)?.details;
  const reason = (details as { reason?: unknown } | null | undefined)?.reason;
  if (typeof reason !== "string") return null;
  return known.includes(reason) ? reason : null;
}

/**
 * The recovery a refusal calls for.
 *
 * `version_unknown` and `hash_mismatch` both mean the published copy changed
 * between the fetch and the purchase — a gym updating terms mid-session, or a
 * tab left open across a deploy. The server's own message asks for a page
 * reload, but a reload would empty the cart the buyer just filled, so the
 * checkout re-fetches in place instead and asks them to confirm the new terms.
 * Same outcome, none of the loss.
 *
 * `locale_invalid` is deliberately NOT the language answer, even though it
 * reads like one. The server raises it when the locale argument is absent or
 * not a string, which this client cannot do — it sends a string constant. It
 * is a code defect, and telling a buyer their language is unsupported would be
 * a false explanation for it. It falls through to the caller's honest generic
 * failure instead.
 */
export function consentRecoveryFor(reason: ConsentReason | null): ConsentRecovery {
  switch (reason) {
    case CONSENT_REASON.versionUnknown:
    case CONSENT_REASON.hashMismatch:
      return "refetch";
    case CONSENT_REASON.missing:
    case CONSENT_REASON.malformed:
      return "reconfirm";
    case CONSENT_REASON.localeUnsupported:
      return "locale";
    default:
      return null;
  }
}

/**
 * What the buyer is told, per recovery. Plain language, no blame, and never a
 * dead end: each one says what happened and what to do next.
 */
export const CONSENT_RECOVERY_MESSAGE: Record<Exclude<ConsentRecovery, null>, string> = {
  refetch: "The membership terms were updated — please review and confirm again.",
  reconfirm: "Please confirm this to start your membership.",
  locale: "This membership can't be bought in this language yet. Please contact your gym.",
};

/* Sibling vocabulary to CONSENT_REASON, never a widening: the statutory
   recoveries refetch and untick the CRD checkbox, which a gym editing its own
   terms must never do. Compiler-bound to the published union. */
export const GYM_TERMS_REASON = {
  missing: "terms_acceptance_missing",
  stale: "terms_stale",
  slugAmbiguous: "terms_slug_ambiguous",
} as const satisfies Record<string, GymTermsRefusalReason>;

export type GymTermsRecovery =
  /** Nothing was sent: render the checkbox from the refusal and ask. */
  | "accept"
  /** The gym republished mid-checkout: ask again against the NEW version/hash. */
  | "reaccept"
  /** Acceptance is required but we cannot honestly present it. Block, don't guess. */
  | "blocked"
  /** Not a gym-terms refusal — the caller's existing paths own it. */
  | null;

/* Returns the whole payload, not just the reason: the checkbox renders from it
   and there is no second summary call to fall back on (owner ruling G13). */
export function gymTermsRefusalOf(error: unknown): GymTermsRefusalDetails | null {
  const details = (error as { details?: unknown } | null | undefined)?.details;
  if (!details || typeof details !== "object") return null;
  if (!refusalTokenOf(error, GYM_TERMS_REFUSAL_REASONS)) return null;
  const record = details as Record<string, unknown>;

  const text = (value: unknown) => (typeof value === "string" ? value : "");
  return {
    reason: record.reason as GymTermsRefusalReason,
    boxID: text(record.boxID),
    termsVersion: text(record.termsVersion),
    termsContentHash: text(record.termsContentHash),
    publicUrl: text(record.publicUrl),
  };
}

/* The single completeness check. gymTermsRefusalOf deliberately does not also
   validate, or loosening this list would be masked by a guard upstream. */
export function isRenderableGymTermsRefusal(
  refusal: GymTermsRefusalDetails | null
): refusal is GymTermsRefusalDetails {
  if (!refusal) return false;
  return (["boxID", "termsVersion", "termsContentHash", "publicUrl"] as const).every(
    (field) => refusal[field] !== ""
  );
}

/* `slug_ambiguous` is mapped though the gate never emits it yet: an unmapped
   token falls through to null and surfaces as a generic checkout error. */
export function gymTermsRecoveryFor(reason: GymTermsRefusalReason | null): GymTermsRecovery {
  switch (reason) {
    case GYM_TERMS_REASON.missing:
      return "accept";
    case GYM_TERMS_REASON.stale:
      return "reaccept";
    case GYM_TERMS_REASON.slugAmbiguous:
      return "blocked";
    default:
      return null;
  }
}

/* Never names the gym: the only name a cart line carries is the product
   creator's account name, frequently a different party. Never says "reload"
   either — a reload empties the cart the buyer just filled. */
export const GYM_TERMS_RECOVERY_MESSAGE: Record<Exclude<GymTermsRecovery, null>, string> = {
  accept: "Your gym requires you to agree to its terms before your membership can start.",
  reaccept: "Your gym's terms have been updated — please review and confirm again.",
  blocked:
    "We can't load your gym's terms right now. Nothing has been charged — please try again in a moment.",
};
